// =============================================================================
// Shared PO extraction / hold classification.
//
// The API tags each uploaded PO's audit trail (attachment.extractionError) with a
// reconcile outcome so the UI can group holds by *why* they're held:
//   • N30 (cash):        "[N30 reconcile: CONFIRM|CONFIRM_AMOUNT_ONLY|HOLD_MISMATCH|HOLD_REVIEW] …"
//   • Installment plans: "[Plan reconcile: OK|HELD|NO-SIGNAL] …"
// plus the extraction status ("Failed") and the duplicate-PO flag.
//
// Used by:
//   • ConvertToOrderModal — per-PO badge in the convert flow's review list.
//   • QuoteReviewQueue     — rolls each quote's held POs up into error-status chips.
// Keep the category keys stable — both callers and the breakdown chips key off them.
// =============================================================================

export const PO_CLASS = {
  confirmed:    { label: "Confirmed",         tone: "success",   dark: false },
  ready:        { label: "Ready to convert",  tone: "success",   dark: false },
  undercovered: { label: "Under-covered",     tone: "danger",    dark: false },
  mismatch:     { label: "Amount mismatch",   tone: "warning",   dark: true  },
  partMiss:     { label: "Part not matched",  tone: "warning",   dark: true  },
  noAmount:     { label: "No amount read",    tone: "warning",   dark: true  },
  planHold:     { label: "Line check failed", tone: "warning",   dark: true  },
  review:       { label: "Needs review",      tone: "warning",   dark: true  },
  failed:       { label: "Extraction failed", tone: "danger",    dark: false },
  duplicate:    { label: "Duplicate PO #",    tone: "danger",    dark: false },
};

const RECONCILE_TAG = /\[(?:N30|Plan) reconcile:\s*([A-Z0-9_-]+)\]/i;

// Classify one PO-like row. Expects { isDuplicate?, held, message, status } where
// `message` is the attachment's extractionError audit string and `held` is true
// when the PO has no confirmed amount.
export function classifyPo(a) {
  if (a.isDuplicate) return "duplicate";
  if (!a.held) return "confirmed";                      // auto- or manually-confirmed
  const msg = a.message || "";
  const outcome = (RECONCILE_TAG.exec(msg)?.[1] || "").toUpperCase();
  const lower = msg.toLowerCase();
  switch (outcome) {
    case "HOLD_MISMATCH": return "mismatch";
    case "HOLD_REVIEW":
      if (lower.includes("reference") || lower.includes("part")) return "partMiss";
      if (lower.includes("no usable total") || lower.includes("no total") || lower.includes("manual entry")) return "noAmount";
      return "review";
    case "HELD":
      // Installment (BK/POT/CTO) hold — sub-classify from the reason text.
      if (lower.includes("reference") || lower.includes("part")) return "partMiss";
      if (lower.includes("amount") || lower.includes("foot") || lower.includes(" over ") || lower.includes(" under ")) return "mismatch";
      return "planHold";
    case "NO-SIGNAL":    return "noAmount";
    case "CONFIRM":
    case "CONFIRM_AMOUNT_ONLY":
    case "OK":           return "confirmed";
    default:
      // No reconcile tag — fall back to the extraction status.
      if ((a.status || "").toLowerCase() === "failed") return "failed";
      return "review";
  }
}

// Display order for chips/breakdowns (most-urgent first).
export const PO_CLASS_ORDER = [
  "undercovered", "mismatch", "partMiss", "noAmount", "planHold", "review", "failed", "duplicate", "confirmed", "ready",
];
